'use client';
// Objectifs qualité mesurables — ISO 15189:2022 §5.5.
//
// C'était l'écart produit que le référentiel déclarait lui-même. Les chiffres
// existaient déjà (le dossier de revue de direction en calcule neuf rubriques) ;
// il manquait la CIBLE et la COURBE.
//
// Trois choses que cet écran refuse de faire :
//
//  · Rendre un verdict sans cible. Un objectif dont la direction n'a pas fixé
//    le niveau d'exigence est mesuré, pas jugé — comme un risque sans seuil
//    d'acceptabilité n'est ni tolérable ni intolérable.
//
//  · Afficher un chiffre qu'on ne sait pas calculer. « Résultats d'EEQ
//    satisfaisants » dit NON CALCULABLE et explique pourquoi, plutôt que de
//    montrer un zéro ou un 2 % faux et alarmant.
//
//  · Confondre un registre vide avec l'absence d'objectifs. « Aucun objectif
//    n'a encore été fixé » n'est pas « le laboratoire n'a pas d'objectifs ».
//
// Et une chose qu'il assume : un relevé est FIGÉ. La courbe montre ce qui a été
// constaté aux dates où on l'a constaté, pas ce que les données d'aujourd'hui
// diraient de mars. Pour revoir mars avec les données d'aujourd'hui, on prend un
// nouveau relevé daté d'aujourd'hui — les deux coexistent, et l'écart entre eux
// est lui-même une information.
import React, { useEffect, useMemo, useState } from 'react';
import { fetchEcriture } from '@/lib/ecrire';
import { BoutonJournal } from '@/components/BoutonJournal';
import { ligneOuvrable, SansOuvrir } from '@/shared/Tableau';
import { useGed, userName } from '@/lib/store';
import { fmtDate } from '@/lib/meta';
import {
  I, card, inputStyle, EmptyState, FilterBar, RefISO,
  FieldError, RequiredHint, useBusy, Selecteur, BoutonCreer } from '@/components/ui';
import { H1 } from './ressources/commun';
import { useFormulaire } from '@/components/Formulaire';
import { Norme } from '@/components/Norme';
import type { GedObjectifsDossier, GedObjectif, GedObjectifReleve, GedIndicateurDef, GedSanteSmq } from '@/lib/types';

import { Bouton } from '@/shared/Bouton';
import { PiedActions } from '@/shared/PiedActions';
const PERIODICITES: Record<string, string> = {
  bimensuelle: 'Bimensuelle (deux fois par mois)', mensuelle: 'Mensuelle',
  trimestrielle: 'Trimestrielle', semestrielle: 'Semestrielle', annuelle: 'Annuelle',
};
const STATUTS: Record<string, { label: string; bg: string; fg: string }> = {
  actif: { label: 'Suivi', bg: 'var(--cyan-50)', fg: 'var(--cyan-700)' },
  suspendu: { label: 'Suspendu', bg: 'var(--amber-50)', fg: 'var(--amber-600)' },
  clos: { label: 'Clos', bg: 'var(--gray-50)', fg: 'var(--text-muted)' },
};

const lbl: React.CSSProperties = {
  display: 'block', fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase',
  letterSpacing: '0.04em', color: 'var(--text-muted)', marginBottom: 4,
};
const encart: React.CSSProperties = {
  background: 'var(--amber-50)', borderInlineStart: '3px solid var(--amber-500)',
  borderRadius: 'var(--radius-sm)', padding: '9px 13px', fontSize: 12.5,
  color: 'var(--text-body)', lineHeight: 1.5,
};

const suffixe = (u: string | null) => (u === 'pourcent' ? ' %' : u === 'jours' ? ' j' : '');
const cibleTexte = (o: GedObjectif) =>
  (o.cible === null || !o.sens ? '' : `${o.sens === 'au_plus' ? '≤' : '≥'} ${o.cible}${suffixe(o.unite)}`);

/** Verdict d'un relevé. Trois issues, et la troisième n'est PAS un échec :
 *  « sans verdict » veut dire qu'on a mesuré sans juger, faute de cible ou
 *  faute de chiffre. La confondre avec « manqué » serait reprocher au
 *  laboratoire de ne pas avoir décidé. */
function Verdict({ r }: { r: GedObjectifReleve }) {
  if (!r.calculable) {
    return (
      <span title={r.motif} style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--amber-600)', cursor: 'help' }}>
        non calculable
      </span>
    );
  }
  if (r.verdict === 'atteint') {
    return <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--green-600)' }}>atteint</span>;
  }
  if (r.verdict === 'manque') {
    return <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--red-600)' }}>manqué</span>;
  }
  return (
    <span title={r.cible === null
      ? 'Aucune cible n’était fixée au moment du relevé : la valeur est mesurée, elle n’est pas jugée.'
      : 'Le sens de la cible n’était pas précisé : « au plus » ou « au moins » ?'}
      style={{ fontSize: 12.5, color: 'var(--text-muted)', fontStyle: 'italic', cursor: 'help' }}>
      sans verdict
    </span>
  );
}

/** La courbe. Un graphe minuscule, tracé sur les relevés CALCULABLES seulement :
 *  relier un point à travers un trou (« non calculable ») dessinerait une
 *  continuité qui n'a pas existé. Les relevés non calculables restent visibles
 *  dans le tableau en dessous — ils ne sont pas cachés, ils ne sont pas tracés. */
function Courbe({ o }: { o: GedObjectif }) {
  const pts = o.releves.filter((r) => r.calculable && r.valeur !== null);
  if (pts.length < 2) return null;
  const vals = pts.map((p) => p.valeur as number);
  const bornes = [...vals, ...(o.cible !== null ? [o.cible] : [])];
  const min = Math.min(...bornes); const max = Math.max(...bornes);
  const etendue = max - min || 1;
  const W = 320; const H = 54;
  const x = (i: number) => (pts.length === 1 ? W / 2 : (i / (pts.length - 1)) * W);
  const y = (v: number) => H - ((v - min) / etendue) * H;
  const d = pts.map((p, i) => `${i ? 'L' : 'M'}${x(i).toFixed(1)},${y(p.valeur as number).toFixed(1)}`).join(' ');

  return (
    <svg width={W} height={H + 8} viewBox={`0 -4 ${W} ${H + 8}`} role="img"
      aria-label={`Évolution sur ${pts.length} relevés, de ${vals[0]} à ${vals[vals.length - 1]}`}
      style={{ overflow: 'visible' }}>
      {o.cible !== null && (
        <line x1={0} x2={W} y1={y(o.cible)} y2={y(o.cible)} stroke="var(--amber-500)"
          strokeWidth={1} strokeDasharray="4 3" />
      )}
      <path d={d} fill="none" stroke="var(--color-primary)" strokeWidth={1.6} />
      {pts.map((p, i) => (
        <circle key={i} cx={x(i)} cy={y(p.valeur as number)} r={2.6}
          fill={p.verdict === 'manque' ? 'var(--red-600)' : p.verdict === 'atteint' ? 'var(--green-600)' : 'var(--text-muted)'}>
          <title>{`${fmtDate(p.dateReleve)} — ${p.valeur}${suffixe(p.unite)}`}</title>
        </circle>
      ))}
    </svg>
  );
}

/** Saisie d'un objectif. La cible et son sens vont ENSEMBLE, et restent
 *  facultatifs : un objectif qu'on mesure sans encore savoir où placer la barre
 *  est un objectif légitime. */
function Formulaire({ ligne, catalogue, onFait, onAnnule, indicateurInitial }: {
  indicateurInitial?: string | null;
  ligne: GedObjectif | null; catalogue: GedIndicateurDef[]; onFait: () => void; onAnnule: () => void;
}) {
  const { settings } = useGed();
  const gedUsers = settings?.gedUsers || [];
  const [f, setF] = useState(() => ({
    libelle: ligne?.libelle || '', description: ligne?.description || '',
    // Présélection venue d'un voyant de santé : on arrive avec l'indicateur en
    // tête, il ne doit pas falloir le retrouver dans une liste de quinze.
    indicateur: ligne?.indicateur || indicateurInitial || '',
    cible: ligne?.cible === null || ligne?.cible === undefined ? '' : String(ligne.cible),
    sens: ligne?.sens || '',
    periodicite: ligne?.periodicite || 'annuelle',
    debutSuivi: ligne?.debutSuivi || '',
    responsable: ligne?.responsable || '',
    statut: ligne?.statut || 'actif',
  }));
  /* ⚠️ CE QUE L'ÉCRAN A LU, FIGÉ À L'OUVERTURE (14/10/2026), sous la forme
     exacte du corps : le moteur n'écrit que ce qui a changé depuis, et refuse
     (409) une rubrique qu'un autre a changée entre-temps. */
  const [base] = useState<Record<string, unknown>>(() => ({
    ...f, cible: f.cible === '' ? null : Number(f.cible), sens: f.cible === '' ? null : f.sens,
  }));
  const [erreur, setErreur] = useState('');
  const { run, estOccupe } = useBusy();

  const maj = (k: string, v: unknown) => setF((x) => ({ ...x, [k]: v }));
  const def = catalogue.find((c) => c.cle === f.indicateur) || null;

  // Choisir l'indicateur PRÉ-COCHE son sens de lecture naturel — un délai se
  // veut court, un taux de clôture se veut haut. C'est une proposition, jamais
  // une décision : le champ reste modifiable, et il n'est jamais rempli seul si
  // aucune cible n'est saisie.
  const choisirIndicateur = (cle: string) => {
    const d = catalogue.find((c) => c.cle === cle);
    setF((x) => ({ ...x, indicateur: cle, sens: x.sens || (d ? d.sensSuggere : '') }));
  };

  /* Les quatre contrôles partent au socle partagé : ils sont maintenant
     annoncés TOUS ENSEMBLE et non un par un. Ce formulaire refusait au premier
     obstacle rencontré — on corrigeait, on recliquait, on découvrait le
     suivant. Trois allers-retours pour quatre règles. */
  const declaration = React.useMemo(() => [
    { id: 'o-lib', label: 'Intitulé', valeur: f.libelle, requis: true },
    { id: 'o-ind', label: 'Indicateur suivi', valeur: f.indicateur, requis: true },
    {
      id: 'o-cible', label: 'Cible', valeur: f.cible,
      erreur: f.cible !== '' && !Number.isFinite(Number(f.cible)) ? 'doit être un nombre' : null,
    },
    {
      id: 'o-sens', label: 'Sens de la cible', valeur: f.sens,
      // Une cible sans sens ne se lit pas : « 5 » veut dire au plus 5 ou au
      // moins 5, et l'écart se juge dans un sens ou dans l'autre.
      erreur: f.cible !== '' && !f.sens ? 'à préciser — « au plus » ou « au moins », sinon la cible ne se lit pas' : null,
    },
  ], [f.libelle, f.indicateur, f.cible, f.sens]);
  const { ref: refFormulaire, verifier, Bandeau } = useFormulaire(declaration);

  const enregistrer = () => run('save', async () => {
    if (!verifier()) return;
    setErreur('');
    const corps: Record<string, unknown> = { ...f, cible: f.cible === '' ? null : Number(f.cible), sens: f.cible === '' ? null : f.sens };
    if (ligne) corps.base = base;
    const r = await fetchEcriture(`/api/ged/ged/objectifs${ligne ? `/${ligne.documentId}` : ''}`, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(corps),
    });
    if (!r.ok) {
      const j = await r.json().catch(() => ({}));
      setErreur(j?.error?.message || 'Enregistrement refusé.');
      return;
    }
    onFait();
  });

  return (
    <div ref={refFormulaire} style={{ ...card, padding: 16, display: 'grid', gap: 12 }}>
      <Bandeau />
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0, fontSize: 14.5, fontWeight: 700, color: 'var(--text-strong)' }}>
          {ligne ? `Modifier ${ligne.ref}` : 'Fixer un objectif qualité'}
        </h2>
        <RequiredHint />
      </div>

      <div>
        <label style={lbl} htmlFor="o-lib">Intitulé <span style={{ color: 'var(--red-600)' }}>*</span></label>
        <input id="o-lib" value={f.libelle} onChange={(e) => maj('libelle', e.target.value)}
          placeholder="Ex. : réduire le délai de clôture des non-conformités" style={inputStyle} />
      </div>

      <div>
        <label style={lbl} htmlFor="o-ind">Indicateur suivi <span style={{ color: 'var(--red-600)' }}>*</span></label>
        <select id="o-ind" value={f.indicateur} onChange={(e) => choisirIndicateur(e.target.value)}
          style={{ ...inputStyle, height: 34, fontSize: 12.5 }}>
          <option value="">— choisir —</option>
          {catalogue.map((c) => <option key={c.cle} value={c.cle}>{c.libelle}</option>)}
        </select>
        <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginTop: 5, lineHeight: 1.5 }}>
          {def
            ? def.aide
            : 'Seuls les indicateurs que le dossier de revue de direction sait déjà calculer sont proposés — '
              + 'c’est la même source, pour que les deux écrans ne racontent jamais deux histoires.'}
        </div>
      </div>

      <div>
        <div style={lbl}>Cible — facultative, et sans valeur par défaut</div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <Selecteur id="o-sens" value={f.sens} onChange={(v) => maj('sens', v)} aria-label="Sens de la cible"
            style={{ ...inputStyle, height: 34, width: 'auto', minWidth: 120, fontSize: 12.5 }}>
            <option value="">— sens —</option>
            <option value="au_plus">au plus (≤)</option>
            <option value="au_moins">au moins (≥)</option>
          </Selecteur>
          <input id="o-cible" type="number" step="any" value={f.cible} aria-label="Valeur cible"
            onChange={(e) => maj('cible', e.target.value)} placeholder="valeur"
            style={{ ...inputStyle, height: 34, width: 120, fontSize: 12.5 }} />
          <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
            {def ? (def.unite === 'pourcent' ? '%' : def.unite === 'jours' ? 'jours' : 'unités') : ''}
          </span>
        </div>
        <div style={{ ...encart, marginTop: 8 }}>
          {f.cible === ''
            ? <>Sans cible, cet objectif sera <strong>mesuré mais jamais jugé</strong> : les relevés porteront la
              valeur atteinte, sans « atteint » ni « manqué ». Le niveau d’exigence est une décision de
              direction — l’outil ne la prend pas à sa place.</>
            : <>La cible sera <strong>recopiée dans chaque relevé</strong>. Si vous la relevez plus tard, les
              relevés déjà pris continueront d’être lus contre celle qui s’appliquait le jour où ils
              ont été pris.</>}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(160px,1fr))', gap: 10 }}>
        <div>
          <label style={lbl} htmlFor="o-per">Rythme de relevé</label>
          <select id="o-per" value={f.periodicite} onChange={(e) => maj('periodicite', e.target.value)}
            style={{ ...inputStyle, height: 34, fontSize: 12.5 }}>
            {Object.entries(PERIODICITES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </div>
        <div>
          <label style={lbl} htmlFor="o-deb">Suivi depuis</label>
          <input id="o-deb" type="date" value={f.debutSuivi} onChange={(e) => maj('debutSuivi', e.target.value)}
            style={{ ...inputStyle, height: 34, fontSize: 12.5 }} />
        </div>
        <div>
          <label style={lbl} htmlFor="o-resp">Responsable</label>
          <Selecteur id="o-resp" value={f.responsable} onChange={(v) => maj('responsable', v)}
            style={{ ...inputStyle, height: 34, fontSize: 12.5 }}>
            <option value="">—</option>
            {gedUsers.map((u) => <option key={u.id} value={u.id}>{u.nom}</option>)}
          </Selecteur>
        </div>
        {ligne && (
          <div>
            <label style={lbl} htmlFor="o-stat">Statut</label>
            <select id="o-stat" value={f.statut} onChange={(e) => maj('statut', e.target.value)}
              style={{ ...inputStyle, height: 34, fontSize: 12.5 }}>
              {Object.entries(STATUTS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
          </div>
        )}
      </div>

      <div>
        <label style={lbl} htmlFor="o-desc">Pourquoi cet objectif</label>
        <textarea id="o-desc" value={f.description} onChange={(e) => maj('description', e.target.value)}
          rows={2} placeholder="Ce que la direction cherche à obtenir, et sur quoi elle s’appuie pour fixer ce niveau."
          style={{ ...inputStyle, height: 'auto', resize: 'vertical' }} />
      </div>

      <FieldError>{erreur}</FieldError>
      <PiedActions taille="sm" annuler={{ onClick: onAnnule }} principale={{ libelle: 'Enregistrer', libelleChargement: 'Enregistrement…', chargement: estOccupe('save'), disabled: estOccupe(), onClick: () => void enregistrer() }} />
    </div>
  );
}

/** Prise d'un relevé. La période est bornée à la main : c'est elle qui définit
 *  ce que la photographie regarde. */
function PriseDeReleve({ o, onFait, onAnnule }: { o: GedObjectif; onFait: () => void; onAnnule: () => void }) {
  const [p, setP] = useState({ periodeDebut: '', periodeFin: '', commentaire: '' });
  const [erreur, setErreur] = useState('');
  const { run, estOccupe } = useBusy();

  const relever = () => run('rel', async () => {
    setErreur('');
    const r = await fetchEcriture(`/api/ged/ged/objectifs/${o.documentId}/releve`, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(p),
    });
    if (!r.ok) {
      const j = await r.json().catch(() => ({}));
      setErreur(j?.error?.message || 'Relevé refusé.');
      return;
    }
    onFait();
  });

  return (
    <div style={{ border: '1px dashed var(--border-strong)', borderRadius: 'var(--radius-md)', padding: 12, marginTop: 8, display: 'grid', gap: 9 }}>
      <div style={{ fontSize: 12.5, color: 'var(--text-body)', lineHeight: 1.5 }}>
        Le relevé calcule l’indicateur sur la période choisie, puis <strong>le fige</strong> : valeur, cible
        du jour et verdict sont écrits une fois pour toutes. À défaut de période, la dernière
        {' '}{PERIODICITES[o.periodicite]?.toLowerCase().replace(/le$/, '')} période échue.
      </div>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div>
          <label style={lbl} htmlFor={`rd-${o.documentId}`}>Période — début</label>
          <input id={`rd-${o.documentId}`} type="date" value={p.periodeDebut}
            onChange={(e) => setP({ ...p, periodeDebut: e.target.value })}
            style={{ ...inputStyle, height: 32, width: 'auto', fontSize: 12.5 }} />
        </div>
        <div>
          <label style={lbl} htmlFor={`rf-${o.documentId}`}>Fin</label>
          <input id={`rf-${o.documentId}`} type="date" value={p.periodeFin}
            onChange={(e) => setP({ ...p, periodeFin: e.target.value })}
            style={{ ...inputStyle, height: 32, width: 'auto', fontSize: 12.5 }} />
        </div>
        <div style={{ flex: '1 1 200px' }}>
          <label style={lbl} htmlFor={`rc-${o.documentId}`}>Commentaire</label>
          <input id={`rc-${o.documentId}`} value={p.commentaire}
            onChange={(e) => setP({ ...p, commentaire: e.target.value })}
            placeholder="Ce que le chiffre ne dit pas…"
            style={{ ...inputStyle, height: 32, fontSize: 12.5 }} />
        </div>
        <Bouton variante="primaire" taille="sm" onClick={() => void relever()} disabled={estOccupe()}>
          {estOccupe('rel') ? 'Relevé…' : 'Prendre le relevé'}
        </Bouton>
        <Bouton taille="sm" onClick={onAnnule}>Annuler</Bouton>
      </div>
      <FieldError>{erreur}</FieldError>
    </div>
  );
}

/** Historique d'un objectif : la suite des photographies, la plus récente en
 *  tête. Chaque ligne porte QUI l'a prise et QUAND — c'est ce qui en fait une
 *  trace, et pas un affichage. */
function Historique({ o }: { o: GedObjectif }) {
  const suite = [...o.releves].reverse();
  return (
    <div style={{ marginTop: 8, border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-md)', overflow: 'hidden' }}>
      {suite.map((r, i) => (
        <div key={r.documentId} style={{ padding: '7px 11px', borderTop: i ? '1px solid var(--border-subtle)' : 'none', display: 'grid', gap: 3 }}>
          <div style={{ display: 'flex', gap: 10, alignItems: 'baseline', flexWrap: 'wrap', fontSize: 12.5 }}>
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12.5, color: 'var(--text-muted)' }}>
              {fmtDate(r.dateReleve)}
            </span>
            <span style={{ color: 'var(--text-muted)', fontSize: 12.5 }}>
              période {fmtDate(r.periodeDebut)} → {fmtDate(r.periodeFin)}
            </span>
            <span style={{ flex: 1 }} />
            <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 700, color: 'var(--text-strong)' }}>
              {r.calculable && r.valeur !== null ? `${r.valeur}${suffixe(r.unite)}` : '—'}
              {r.denominateur !== null && r.numerateur !== null && r.unite === 'pourcent' && (
                <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}> ({r.numerateur}/{r.denominateur})</span>
              )}
            </span>
            {r.cible !== null && r.sens && (
              <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
                cible {r.sens === 'au_plus' ? '≤' : '≥'} {r.cible}{suffixe(r.unite)}
              </span>
            )}
            <Verdict r={r} />
          </div>
          {/* ⚠️ UN DÉPLIANT, PAS UN PAVÉ (relevé SYS-12 du 26/08/2026 : « pavé de
              8 lignes dans une tuile KPI, casse la grille »).
              Le motif de non-calculabilité est long par nature — il explique
              pourquoi un chiffre ne peut PAS être rendu, ce qui demande plus de
              mots qu'un chiffre. Rendu en clair dans une tuile calibrée pour une
              valeur, il faisait tripler sa hauteur et désalignait toute la
              rangée : la grille des indicateurs se lit d'un coup d'œil, et une
              tuile qui dépasse attire l'œil pour une raison qui n'est pas la
              bonne.
              ⚠️ Il n'est pas MASQUÉ pour autant — c'est la phrase qui empêche de
              lire « 0 % » là où il n'y a rien à mesurer, et elle vaut d'être
              lue. `<details>` la garde à un clic, et le résumé dit qu'elle
              existe. */}
          {!r.calculable && r.motif && (
            <details style={{ fontSize: 12.5, color: 'var(--text-body)', lineHeight: 1.5 }}>
              <summary style={{ cursor: 'pointer', color: 'var(--amber-600)', fontWeight: 600 }}>
                Pourquoi ce chiffre n’est pas calculable
              </summary>
              <div style={{ marginTop: 5 }}>{r.motif}</div>
            </details>
          )}
          {r.commentaire && (
            <div style={{ fontSize: 12.5, color: 'var(--text-body)' }}>{r.commentaire}</div>
          )}
          <div style={{ fontSize: 12.5, color: 'var(--text-faint)' }}>
            relevé par {r.releveParNom || '—'}
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * SANTÉ DU SYSTÈME QUALITÉ — huit voyants en direct.
 *
 * Demande de la RQ le 14/08/2026, en attendant les décisions sur l'IA : « des
 * KPI qui évoluent en temps réel pour me donner la santé de mon système qualité
 * en général ».
 *
 * ── Ce que ces voyants disent, et ce qu'ils NE DISENT PAS ───────────────────
 *
 * Ils disent un NIVEAU (la mesure sur 90 jours glissants) et un MOUVEMENT (par
 * rapport au trimestre précédent). Ils ne disent pas si le niveau est bon : cela
 * suppose une cible, et une cible est une décision de direction — elle se fixe
 * en créant un objectif, juste dessous. Une flèche verte ne signifie donc pas
 * « conforme », elle signifie « cela s'améliore ».
 *
 * Pas de note globale sur 100, délibérément : additionner un taux de clôture,
 * un délai médian et un nombre d'habilitations échues suppose des poids que
 * personne n'a arbitrés, et une note unique cache exactement ce qu'on veut voir
 * — QUEL voyant recule.
 */
function SanteSmq({ sante, onFixerCible }: { sante: GedSanteSmq; onFixerCible: (cle: string) => void }) {
  const FLECHE: Record<string, string> = { hausse: '↑', baisse: '↓', stable: '=', inconnue: '·' };
  return (
    <section style={{ display: 'grid', gap: 10 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0, fontSize: 14.5, fontWeight: 700, color: 'var(--text-strong)' }}>Santé du système qualité</h2>
        <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
          {fmtDate(sante.fenetre.debut)} → {fmtDate(sante.fenetre.fin)} ({sante.fenetre.jours} jours),
          comparé au trimestre précédent
        </span>
      </div>

      {/* Le seul chiffre agrégé est un DÉCOMPTE, pas un score. */}
      <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-body)', lineHeight: 1.5, maxWidth: '84ch' }}>
        {sante.enRecul === 0
          ? 'Aucun voyant ne recule sur le trimestre.'
          : <><strong>{sante.enRecul} voyant{sante.enRecul > 1 ? 's reculent' : ' recule'}</strong> par rapport au trimestre précédent.</>}
        {' '}Ces voyants mesurent, ils ne jugent pas : <strong>aucun seuil n’est fixé ici</strong>. Pour
        qu’un écart soit prononcé, il faut une cible — c’est ce que fait un objectif qualité.
        {sante.nonCalculables > 0 && ` ${sante.nonCalculables} indicateur n’est pas calculable et le dit plutôt que d’afficher zéro.`}
      </p>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(238px, 1fr))', gap: 10 }}>
        {sante.voyants.map((v) => {
          const teinte = v.evolutionFavorable === null ? 'var(--border-default)'
            : v.evolutionFavorable ? 'var(--green-600)' : 'var(--amber-600)';
          return (
            <div key={v.cle} style={{ ...card, padding: '12px 14px', borderTop: `3px solid ${teinte}`, display: 'grid', gap: 5 }}>
              <div style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--text-muted)', lineHeight: 1.35 }}>{v.libelle}</div>
              {v.calculable ? (
                <>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 18, fontWeight: 700, color: 'var(--text-strong)' }}>{v.texte}</span>
                    <span title={
                      v.tendance === 'inconnue' ? 'Pas de mesure comparable sur le trimestre précédent'
                        : v.tendance === 'stable' ? 'Variation inférieure au seuil de bruit'
                          : `${v.ecart! > 0 ? '+' : ''}${v.ecart} par rapport au trimestre précédent`
                    } style={{ fontSize: 12.5, fontWeight: 700, color: teinte, cursor: 'help' }}>
                      {FLECHE[v.tendance]}{v.ecart !== null && v.tendance !== 'stable' && v.tendance !== 'inconnue'
                        ? ` ${v.ecart > 0 ? '+' : ''}${v.ecart}` : ''}
                    </span>
                  </div>
                  <div style={{ fontSize: 12.5, color: 'var(--text-faint)', lineHeight: 1.4 }}>
                    {v.tendance === 'inconnue'
                      ? 'Aucune comparaison possible — le trimestre précédent n’a pas de mesure.'
                      : `Se lit bien ${v.sensSuggere === 'au_moins' ? 'à la hausse' : 'à la baisse'}.`}
                  </div>
                </>
              ) : (
                /* Un indicateur non calculable DIT pourquoi. Afficher 0 % de
                   résultats d'évaluation externe satisfaisants parce que le
                   registre repris ne porte que les actions correctives serait
                   faux ET alarmant. */
                <div style={{ fontSize: 12.5, color: 'var(--amber-700)', lineHeight: 1.45 }}>
                  Non calculable — {v.motif}
                </div>
              )}
              {/* ⚠️ UN BOUTON, PAS UN FAUX LIEN (RQ, 26/08/2026 : « il a l'allure
                  d'un lien hypertexte souligné, que je ne veux pas dans
                  l'application ; à reprendre au même format que les autres
                  boutons d'action »). Un geste qui écrit doit se présenter comme
                  les autres gestes qui écrivent — sans quoi on ne sait pas s'il
                  navigue ou s'il agit. */}
              <Bouton taille="sm" type="button" onClick={() => onFixerCible(v.cle)} title="Fixer une cible sur cet indicateur : c’est elle qui permettra de prononcer un écart" style={{ justifySelf: 'start', marginTop: 6 }}>
                <I n="target" s={12} /> Fixer une cible
              </Bouton>
            </div>
          );
        })}
      </div>
    </section>
  );
}

export function Objectifs() {
  const { session, settings } = useGed();
  const isRQ = session.roles.includes('rq');
  const [d, setD] = useState<GedObjectifsDossier | null>(null);
  const [chargement, setChargement] = useState(true);
  const [tour, setTour] = useState(0);
  const [q, setQ] = useState('');
  const [filtre, setFiltre] = useState('tous');
  const [edite, setEdite] = useState<GedObjectif | null>(null);
  const [saisie, setSaisie] = useState(false);
  const [releveDe, setReleveDe] = useState<string | null>(null);
  const [ouvert, setOuvert] = useState<Record<string, boolean>>({});
  /** Indicateur présélectionné quand on arrive depuis un voyant de santé : le
   *  geste « ce chiffre recule, je lui fixe une cible » ne doit pas obliger à
   *  retrouver l'indicateur dans une liste de quinze. */
  const [indicateurPreselect, setIndicateurPreselect] = useState<string | null>(null);

  // `.then()` et non `await` dans l'effet : une fonction `async` appelée
  // directement masque le fait que les `setState` suivent le premier await.
  useEffect(() => {
    let vivant = true;
    void fetch('/api/ged/ged/objectifs', { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => { if (vivant) setD(j); })
      .catch(() => { if (vivant) setD(null); })
      .finally(() => { if (vivant) setChargement(false); });
    return () => { vivant = false; };
  }, [tour]);

  const recharger = () => { setSaisie(false); setEdite(null); setReleveDe(null); setTour((t) => t + 1); };

  const filtrees = useMemo(() => {
    const base = (d?.lignes || []).filter((l) => (
      filtre === 'sanscible' ? l.sansCible
        : filtre === 'sansreleve' ? l.sansReleve
          : filtre === 'manques' ? l.dernier?.verdict === 'manque'
            : filtre === 'noncalc' ? !!l.dernier && !l.dernier.calculable
              : true));
    const qq = q.trim().toLowerCase();
    return qq
      ? base.filter((l) => `${l.ref} ${l.libelle} ${l.indicateurLibelle}`.toLowerCase().includes(qq))
      : base;
  }, [d, filtre, q]);

  if (chargement) return <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>Chargement des objectifs…</p>;
  if (!d) return null;
  const { compte, catalogue } = d;

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <H1 eyebrow={<>Pilotage · <RefISO code="5.5" /><Norme art="5.5" /></>} title="Objectifs qualité" />

      {d?.sante && <SanteSmq sante={d.sante} onFixerCible={(cle) => { setIndicateurPreselect(cle); setEdite(null); setSaisie(true); }} />}

      <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-body)', lineHeight: 1.5, maxWidth: '84ch' }}>
        Un objectif suit un <strong>indicateur que le dossier de revue de direction sait déjà calculer</strong> —
        la même source, jamais un second calcul. Chaque relevé est une <strong>photographie datée</strong> :
        sa valeur et la cible du jour y sont figées, pour qu’un objectif atteint en mars ne devienne
        pas manqué en juin parce qu’un enregistrement de mars a bougé depuis.
      </p>

      {compte.sansCible > 0 && (
        <div style={encart}>
          <strong style={{ color: 'var(--text-strong)' }}>
            {compte.sansCible} objectif{compte.sansCible > 1 ? 's' : ''} sans cible fixée.
          </strong>{' '}
          Ils sont mesurés, pas jugés : aucun verdict « atteint » ni « manqué » n’est rendu tant que
          la direction n’a pas dit où placer la barre, ni dans quel sens elle se lit.
        </div>
      )}
      {compte.nonCalculables > 0 && (
        <div style={encart}>
          <strong style={{ color: 'var(--text-strong)' }}>
            {compte.nonCalculables} objectif{compte.nonCalculables > 1 ? 's' : ''} dont le dernier relevé
            n’est <em>pas calculable</em>.
          </strong>{' '}
          Le chiffre n’existe pas dans les données reprises — l’écran le dit plutôt que d’afficher un
          zéro qui se lirait comme un résultat.
        </div>
      )}

      {compte.total === 0 && !saisie && (
        <EmptyState
          titre="Aucun objectif qualité n’a encore été fixé."
          indice="Ce n’est pas « le laboratoire n’a pas d’objectifs » : c’est que rien n’a encore été inscrit ici. La direction établit ses objectifs mesurables, l’outil les suit — et c’est exactement ce que le dossier de revue de direction affichera tant que ce registre reste vide."
          action={isRQ ? (
            <Bouton variante="primaire" onClick={() => setSaisie(true)}>
              <I n="plus" s={14} />Fixer le premier objectif
            </Bouton>
          ) : undefined} />
      )}

      {(saisie || edite) && isRQ && (
        <Formulaire ligne={edite} catalogue={catalogue} onFait={recharger}
          indicateurInitial={indicateurPreselect}
          onAnnule={() => { setSaisie(false); setEdite(null); setIndicateurPreselect(null); }} />
      )}

      {compte.total > 0 && (
        <>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: 260 }}>
              <FilterBar
                q={q} onQ={setQ} placeholder="Rechercher un objectif, un indicateur…"
                chips={[
                  { k: 'tous', label: 'Tous', n: compte.total },
                  { k: 'sanscible', label: 'Sans cible', n: compte.sansCible },
                  { k: 'sansreleve', label: 'Jamais relevés', n: compte.sansReleve },
                  { k: 'manques', label: 'Derniers relevés manqués', n: compte.manques },
                  { k: 'noncalc', label: 'Non calculables', n: compte.nonCalculables },
                ]}
                actif={filtre} onChip={setFiltre}
                total={compte.total} affiches={filtrees.length}
                onReset={() => { setQ(''); setFiltre('tous'); }} />
            </div>
            {/* Bleu PLEIN et non plus le cyan pâle : la règle de la maison
                (RQ, 19/08) vaut aussi ici — un bouton de création qui ne
                ressemble pas aux autres ne se trouve pas. */}
            {isRQ && !saisie && !edite && (
              <BoutonCreer onClick={() => setSaisie(true)}>Fixer un objectif</BoutonCreer>
            )}
          </div>

          <div style={{ ...card, padding: 0, overflow: 'hidden' }}>
            {filtrees.map((l, i) => {
              const st = STATUTS[l.statut] || STATUTS.actif;
              const ct = cibleTexte(l);
              return (
                /* Le clic ouvre l'objectif ; « Prendre un relevé » et le dépli
                   des relevés restent — ce ne sont pas le geste par défaut
                   (Ines, 26/08/2026). */
                <div key={l.documentId}
                  {...(isRQ
                    ? ligneOuvrable(() => { setSaisie(false); setEdite(l); }, `Modifier ${l.ref}`,
                      { padding: '11px 14px', borderTop: i ? '1px solid var(--border-subtle)' : 'none' })
                    : { style: { padding: '11px 14px', borderTop: i ? '1px solid var(--border-subtle)' : 'none' } })}>
                  <div style={{ display: 'flex', gap: 10, alignItems: 'baseline', flexWrap: 'wrap' }}>
                    <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12.5, fontWeight: 700, color: 'var(--color-primary-active)' }}>{l.ref}</span>
                    {/* La traçabilité au même endroit que partout : l'icône du
                        journal suit la référence (règle RQ du 18/08, câblée le
                        24/08/2026 — ce registre écrivait au journal sans le
                        montrer). */}
                    <SansOuvrir><BoutonJournal reference={l.ref} quoi="de cet objectif" /></SansOuvrir>
                    <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-strong)' }}>{l.libelle}</span>
                    <span style={{ fontSize: 10.5, fontWeight: 700, borderRadius: 'var(--radius-pill)', padding: '2px 9px', background: st.bg, color: st.fg }}>{st.label}</span>
                    <span style={{ flex: 1 }} />
                    {l.dernier ? (
                      <>
                        <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12.5, fontWeight: 700, color: 'var(--text-strong)' }}>
                          {l.dernier.calculable && l.dernier.valeur !== null ? `${l.dernier.valeur}${suffixe(l.unite)}` : '—'}
                        </span>
                        <Verdict r={l.dernier} />
                      </>
                    ) : (
                      <span title="Un objectif qui ne se relève pas ne se suit pas : il n’a ni valeur atteinte, ni courbe."
                        style={{ fontSize: 12.5, color: 'var(--amber-600)', fontWeight: 700, cursor: 'help' }}>
                        jamais relevé
                      </span>
                    )}
                  </div>

                  <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 4, fontSize: 12.5, color: 'var(--text-muted)', alignItems: 'center' }}>
                    <span>
                      {l.indicateurConnu ? l.indicateurLibelle : (
                        <span style={{ color: 'var(--red-600)', fontWeight: 700 }}>
                          indicateur « {l.indicateur} » absent du catalogue
                        </span>
                      )}
                    </span>
                    <span style={{ fontWeight: 700, color: ct ? 'var(--text-body)' : 'var(--amber-600)' }}>
                      {ct ? `cible ${ct}` : 'aucune cible fixée'}
                    </span>
                    <span>{PERIODICITES[l.periodicite] || l.periodicite}</span>
                    {l.responsable && <span>{userName(settings, l.responsable)}</span>}
                    <span style={{ flex: 1 }} />
                    <SansOuvrir>
                    {l.releves.length > 0 && (
                      <Bouton taille="sm" onClick={() => setOuvert((o) => ({ ...o, [l.documentId]: !o[l.documentId] }))}>
                        {ouvert[l.documentId] ? 'Masquer' : `${l.releves.length} relevé${l.releves.length > 1 ? 's' : ''}`}
                      </Bouton>
                    )}
                    {isRQ && (
                      <>
                        {l.statut !== 'clos' && (
                          <Bouton taille="sm" onClick={() => setReleveDe(releveDe === l.documentId ? null : l.documentId)} title="Calculer l’indicateur sur une période et FIGER le résultat. Le relevé ne sera plus recalculé.">
                            Prendre un relevé
                          </Bouton>
                        )}
                      </>
                    )}
                    </SansOuvrir>
                  </div>

                  {l.description && (
                    <div style={{ fontSize: 12.5, color: 'var(--text-body)', marginTop: 4 }}>{l.description}</div>
                  )}
                  {l.releves.filter((r) => r.calculable).length > 1 && (
                    <div style={{ marginTop: 6 }}><Courbe o={l} /></div>
                  )}
                  {releveDe === l.documentId && isRQ && (
                    <PriseDeReleve o={l} onFait={recharger} onAnnule={() => setReleveDe(null)} />
                  )}
                  {ouvert[l.documentId] && l.releves.length > 0 && <Historique o={l} />}
                </div>
              );
            })}
            {filtrees.length === 0 && (
              <div style={{ padding: 16 }}>
                <EmptyState titre="Aucun objectif ne correspond à ces critères."
                  action={<Bouton taille="sm" onClick={() => { setQ(''); setFiltre('tous'); }}>Réinitialiser</Bouton>} />
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
